/* ==========================================================================
   TODAY Fitness Equipment — theme behaviour
   Idempotent initialisers so the Shopify theme editor can reload a section
   without double-binding. Everything degrades to a static page without JS.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var mark = function (el, key) {
    if (el.dataset[key] === 'on') return false;
    el.dataset[key] = 'on';
    return true;
  };

  /* ---------------------------------------------------------------- header */

  function initHeader(root) {
    var header = (root || document).querySelector('[data-tf-header]');
    if (!header || !mark(header, 'tfHeader')) return;

    var threshold = parseInt(header.dataset.tfThreshold || '80', 10);
    var transparent = header.dataset.tfTransparent === 'true';

    function sync() {
      var solid = !transparent || window.scrollY > threshold;
      header.classList.toggle('is-solid', solid);
    }
    sync();
    window.addEventListener('scroll', sync, { passive: true });

    var burger = header.querySelector('[data-tf-burger]');
    var drawer = document.querySelector('[data-tf-drawer]');
    if (!burger || !drawer) return;
    var icon = burger.querySelector('use');

    function setOpen(open) {
      drawer.hidden = !open;
      burger.setAttribute('aria-expanded', String(open));
      burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      if (icon) icon.setAttribute('href', open ? '#tf-i-close' : '#tf-i-menu');
      document.body.style.overflow = open ? 'hidden' : '';
    }
    burger.addEventListener('click', function () { setOpen(drawer.hidden); });
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('a')) setOpen(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !drawer.hidden) setOpen(false);
    });
  }

  /* ------------------------------------------------------------- carousels */

  function initCarousels(root) {
    (root || document).querySelectorAll('[data-tf-carousel]').forEach(function (el) {
      if (!mark(el, 'tfCarousel')) return;

      var track = el.querySelector('[data-tf-track]');
      var prev = el.querySelector('[data-tf-prev]');
      var next = el.querySelector('[data-tf-next]');
      if (!track) return;

      function step() {
        var first = track.firstElementChild;
        if (!first) return 320;
        var gap = parseFloat(getComputedStyle(track).columnGap || '16') || 16;
        return first.getBoundingClientRect().width + gap;
      }
      function sync() {
        if (!prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      }
      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: reduce ? 'auto' : 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: reduce ? 'auto' : 'smooth' });
      });
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();

      /* pointer drag */
      var down = false, startX = 0, startLeft = 0, moved = false;
      track.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        down = true; moved = false;
        startX = e.clientX; startLeft = track.scrollLeft;
      });
      track.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4) {
          if (!moved) track.classList.add('is-dragging');
          moved = true;
          track.scrollLeft = startLeft - dx;
        }
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
        track.addEventListener(ev, function () {
          down = false;
          track.classList.remove('is-dragging');
        });
      });
      track.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);
    });
  }

  /* --------------------------------------------------------- story slider */

  function initStories(root) {
    (root || document).querySelectorAll('[data-tf-stories]').forEach(function (el) {
      if (!mark(el, 'tfStories')) return;

      var track = el.querySelector('[data-tf-stories-track]');
      var dotsBox = el.querySelector('[data-tf-dots]');
      var pauseBtn = el.querySelector('[data-tf-pause]');
      if (!track) return;

      var slides = Array.prototype.slice.call(track.children);
      if (slides.length < 2) {
        if (el.querySelector('[data-tf-stories-ctl]')) {
          el.querySelector('[data-tf-stories-ctl]').hidden = true;
        }
        return;
      }

      var dur = parseInt(el.dataset.tfInterval || '7000', 10);
      el.style.setProperty('--tf-dot-dur', dur + 'ms');

      var index = 0, timer = null, paused = false;
      var dots = [];

      if (dotsBox) {
        dotsBox.innerHTML = '';
        slides.forEach(function (_, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'tf-dot';
          b.setAttribute('role', 'tab');
          b.setAttribute('aria-label', 'Slide ' + (i + 1));
          b.addEventListener('click', function () { go(i); });
          dotsBox.appendChild(b);
        });
        dots = Array.prototype.slice.call(dotsBox.children);
      }

      function render() {
        track.style.transform = 'translateX(' + (-index * 100) + '%)';
        slides.forEach(function (s, i) {
          s.setAttribute('aria-hidden', String(i !== index));
        });
        dots.forEach(function (d, i) {
          if (i === index) {
            d.setAttribute('aria-current', 'true');
            d.classList.toggle('is-paused', paused);
          } else {
            d.removeAttribute('aria-current');
            d.classList.remove('is-paused');
          }
        });
      }
      function go(i) {
        index = (i + slides.length) % slides.length;
        if (dots[index]) {
          dots[index].removeAttribute('aria-current');
          void dots[index].offsetWidth;   /* restart the fill */
        }
        render();
        schedule();
      }
      function schedule() {
        clearTimeout(timer);
        if (paused || reduce) return;
        timer = setTimeout(function () { go(index + 1); }, dur);
      }
      function setPaused(p) {
        paused = p;
        if (pauseBtn) {
          var u = pauseBtn.querySelector('use');
          if (u) u.setAttribute('href', p ? '#tf-i-play' : '#tf-i-pause');
          pauseBtn.setAttribute('aria-label', p ? 'Play slideshow' : 'Pause slideshow');
        }
        if (dots[index]) dots[index].classList.toggle('is-paused', p);
        if (p) clearTimeout(timer); else schedule();
      }

      if (pauseBtn) pauseBtn.addEventListener('click', function () { setPaused(!paused); });

      /* pause when off-screen — no point animating what nobody sees */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) { if (!paused) schedule(); }
            else clearTimeout(timer);
          });
        }, { threshold: 0.2 }).observe(el);
      }

      render();
      if (reduce) setPaused(true); else schedule();
    });
  }

  /* ------------------------------------------------------------ hero video */

  function initHeroVideo(root) {
    (root || document).querySelectorAll('[data-tf-hero-video]').forEach(function (el) {
      if (!mark(el, 'tfHeroVideo')) return;
      var video = el.querySelector('video');
      if (!video) return;

      video.muted = true;
      video.playsInline = true;

      function attempt() {
        var p = video.play();
        if (p && p.catch) p.catch(function () { /* autoplay refused — poster stands in */ });
      }
      if (reduce) { video.pause(); } else { attempt(); }

      /* don't burn battery on a video scrolled past */
      if ('IntersectionObserver' in window && !reduce) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) attempt(); else video.pause();
          });
        }, { threshold: 0.15 }).observe(el);
      }

      var toggle = el.querySelector('[data-tf-video-toggle]');
      if (toggle) {
        toggle.addEventListener('click', function () {
          var u = toggle.querySelector('use');
          if (video.paused) {
            video.play();
            if (u) u.setAttribute('href', '#tf-i-pause');
            toggle.setAttribute('aria-label', 'Pause video');
          } else {
            video.pause();
            if (u) u.setAttribute('href', '#tf-i-play');
            toggle.setAttribute('aria-label', 'Play video');
          }
        });
      }
    });
  }

  /* -------------------------------------------------------------- marquee */

  function initMarquee(root) {
    (root || document).querySelectorAll('[data-tf-marquee]').forEach(function (el) {
      if (!mark(el, 'tfMarquee')) return;
      var track = el.querySelector('[data-tf-marquee-track]');
      if (!track || track.children.length === 0) return;
      /* duplicate once so the -50% translate loops seamlessly */
      track.innerHTML = track.innerHTML + track.innerHTML;
    });
  }

  /* --------------------------------------------------------------- reveal */

  function initReveal(root) {
    var els = (root || document).querySelectorAll('.tf-reveal');
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (e) { if (mark(e, 'tfReveal')) io.observe(e); });
  }

  /* -------------------------------------------------------------- booking */
  /* Every booking CTA is a real <a href="{calendly url}"> carrying
     data-tf-book. If Calendly's widget script is present we intercept the
     click and open the popup; if it never loaded, the click falls through and
     the link navigates. A booking button is never dead. */

  function brandedBookingUrl(raw) {
    var cfg = window.TF_BOOKING || {};
    if (!raw) return raw;
    var params = [];
    /* We brand the frame, never the controls. primary_color is what Calendly
       paints the selectable date numerals with, and a saturated green grid of
       them is the opposite of clean — so it is not passed at all. Calendly
       keeps its own accent and its widget stays legible. */
    if (cfg.brand) {
      params.push('background_color=' + (cfg.background || '0A0A0A'));
      params.push('text_color=' + (cfg.text || 'FFFFFF'));
    }
    /* Independent of branding — it was previously trapped inside the brand
       branch, so switching branding off silently brought the banner back. */
    if (cfg.hideCookie) params.push('hide_gdpr_banner=1');
    if (!params.length) return raw;
    var sep = raw.indexOf('?') === -1 ? '?' : '&';
    return raw + sep + params.join('&');
  }

  function closeBooking() {
    if (window.Calendly && window.Calendly.closePopupWidget) {
      window.Calendly.closePopupWidget();
      return true;
    }
    /* Belt and braces: if their API is not there, take the overlay out. */
    var o = document.querySelector('.calendly-overlay');
    if (o && o.parentNode) { o.parentNode.removeChild(o); return true; }
    return false;
  }

  function initBooking() {
    if (document.documentElement.dataset.tfBooking === 'on') return;
    document.documentElement.dataset.tfBooking = 'on';

    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[data-tf-book]');
      if (!a) return;
      var cfg = window.TF_BOOKING || {};
      if (!cfg.popup) return;                    /* configured to open a tab */
      if (!window.Calendly || !window.Calendly.initPopupWidget) return;  /* let the link work */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; /* open-in-new-tab intent */
      e.preventDefault();
      window.Calendly.initPopupWidget({ url: brandedBookingUrl(a.getAttribute('href')) });
    });

    /* Calendly ships its own close control, but it is a small sprite pinned to
       the popup corner and people were not finding it. Two more ways out, both
       of which people try by reflex. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (!document.querySelector('.calendly-overlay')) return;
      closeBooking();
    });

    document.addEventListener('click', function (e) {
      var overlay = document.querySelector('.calendly-overlay');
      if (!overlay) return;
      /* The dimmed area outside the card, and the close control itself.
         Matching .calendly-popup-close matters: if their own handler is not
         attached the X is inert, which is the state this started in. Calling
         closePopupWidget twice is harmless — the second call finds nothing. */
      var hit = e.target === overlay ||
                (e.target.closest && e.target.closest('.calendly-popup-close,[data-tf-book-close]'));
      if (hit) {
        e.preventDefault();
        closeBooking();
      }
    });

    /* Their close button is inserted with the overlay, so it is not there to
       style at load. Watch for the overlay and label it for assistive tech. */
    if (window.MutationObserver) {
      new MutationObserver(function () {
        var btn = document.querySelector('.calendly-popup-close');
        if (btn && !btn.getAttribute('aria-label')) {
          btn.setAttribute('aria-label', 'Close booking');
          btn.setAttribute('role', 'button');
          btn.setAttribute('tabindex', '0');
        }
      }).observe(document.body, { childList: true });
    }
  }

  /* The machine a visitor came from. Product pages link to
     /pages/contact?machine=<title>, so the booking page can name the enquiry
     and hand it to Calendly. Read as text and never written as HTML. */
  function bookingContext() {
    try {
      var q = new URLSearchParams(window.location.search);
      var v = q.get('machine') || q.get('product') || '';
      return v.trim().slice(0, 120);
    } catch (e) { return ''; }
  }

  function showBookingContext(root, name) {
    (root || document).querySelectorAll('[data-tf-booking-context]').forEach(function (row) {
      var slot = row.querySelector('[data-tf-booking-context-name]');
      if (!name || !slot) return;
      slot.textContent = name;
      row.hidden = false;
    });
  }

  /* inline embeds */
  function initBookingEmbeds(root) {
    var context = bookingContext();
    showBookingContext(root, context);

    (root || document).querySelectorAll('[data-tf-booking-embed]').forEach(function (el) {
      if (!mark(el, 'tfEmbed')) return;
      var url = el.dataset.tfUrl;
      if (!url) return;
      function build() {
        if (!window.Calendly || !window.Calendly.initInlineWidget) return false;
        var full = brandedBookingUrl(url);
        if (context) {
          /* a1 is Calendly's first custom question. An event without one
             ignores it, so this is safe whatever the calendar is set up with. */
          full += (full.indexOf('?') === -1 ? '?' : '&') + 'a1=' + encodeURIComponent(context);
        }
        el.innerHTML = '';
        /* States a real height for Calendly's height="100%" iframe. Added
           before the widget is built so it lays out against the final frame. */
        el.classList.add('is-live');
        window.Calendly.initInlineWidget({ url: full, parentElement: el });
        return true;
      }
      if (build()) return;
      var tries = 0;
      var t = setInterval(function () {
        if (build() || ++tries > 40) clearInterval(t);   /* ~10s, then give up
                                                            and leave the
                                                            fallback link */
      }, 250);
    });
  }

  /* --------------------------------------------------------- in-page nav */
  /* A menu item pointing at /#process is a full URL to Shopify, so the browser
     reloads the homepage and jumps. Intercept the ones that resolve to the page
     we are already on and scroll instead. The fixed header would otherwise sit
     on top of the heading, so the offset comes off the same variable the header
     is sized from. */

  function headerOffset() {
    /* Measure rather than trust a token. The header is fixed, so its rect is
       already viewport-relative and .bottom is exactly what a target has to
       clear — which covers the pill's top gap without knowing about it, at
       every breakpoint. */
    var h = document.querySelector('[data-tf-header]');
    if (h) {
      var bottom = h.getBoundingClientRect().bottom;
      if (bottom > 0) return Math.round(bottom) + 12;
    }
    var v = getComputedStyle(document.documentElement).getPropertyValue('--tf-nav-total') ||
            getComputedStyle(document.documentElement).getPropertyValue('--tf-nav-h');
    var n = parseInt(v, 10);
    return (isNaN(n) ? 80 : n) + 12;
  }

  function scrollToTarget(el, smooth) {
    var y = el.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, y), behavior: (smooth && !reduce) ? 'smooth' : 'auto' });
  }

  function samePageHash(a) {
    if (!a || !a.getAttribute) return null;
    var href = a.getAttribute('href');
    if (!href || href.charAt(0) === '#') return href ? href.slice(1) : null;
    /* resolve against the current page and compare path, ignoring the hash */
    var u;
    try { u = new URL(a.href, window.location.href); } catch (e) { return null; }
    if (!u.hash || u.hash === '#') return null;
    if (u.origin !== window.location.origin) return null;
    if (u.pathname.replace(/\/$/, '') !== window.location.pathname.replace(/\/$/, '')) return null;
    return decodeURIComponent(u.hash.slice(1));
  }

  function initAnchors() {
    if (document.documentElement.dataset.tfAnchors === 'on') return;
    document.documentElement.dataset.tfAnchors = 'on';

    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      if (a.hasAttribute('data-tf-book')) return;         /* booking owns its click */
      if (a.target && a.target !== '_self') return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;

      var id = samePageHash(a);
      if (!id) return;
      var el = document.getElementById(id);
      if (!el) return;                                     /* let it navigate */

      e.preventDefault();
      scrollToTarget(el, true);
      if (history.replaceState) history.replaceState(null, '', '#' + id);

      /* a tap in the mobile drawer should close it on the way */
      var drawer = document.querySelector('[data-tf-drawer]');
      if (drawer && !drawer.hidden && drawer.contains(a)) {
        var burger = document.querySelector('[data-tf-burger]');
        if (burger) burger.click();
      }
    });

    /* Arriving with a hash: the browser jumps before the header is measured,
       so put it right once on load. */
    if (window.location.hash.length > 1) {
      var target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
      if (target) {
        window.requestAnimationFrame(function () {
          window.setTimeout(function () { scrollToTarget(target, false); }, 60);
        });
      }
    }
  }

  /* -------------------------------------------------- cookie preferences */
  /* The control stays hidden until Shopify's customer privacy API is present
     and can actually reopen the banner. A button that claims to manage consent
     and does nothing is worse than no button. */

  function initCookieSettings() {
    var btns = document.querySelectorAll('[data-tf-cookie-settings]');
    if (!btns.length) return;

    function api() {
      return (window.Shopify && window.Shopify.customerPrivacy) || null;
    }

    function show() {
      var a = api();
      if (!a || typeof a.showPreferences !== 'function') return false;
      btns.forEach(function (b) {
        if (!mark(b, 'tfCookie')) return;
        b.hidden = false;
        b.addEventListener('click', function () {
          try { a.showPreferences(); } catch (e) { /* nothing else to offer */ }
        });
      });
      return true;
    }

    if (show()) return;
    /* Shopify loads its privacy bundle asynchronously; give it a window, then
       leave the control hidden rather than guessing. */
    var tries = 0;
    var t = setInterval(function () {
      if (show() || ++tries > 20) clearInterval(t);
    }, 500);
  }

  /* ------------------------------------------------------------------ init */

  function initAll(root) {
    initAnchors();
    initCookieSettings();
    initBooking();
    initBookingEmbeds(root);
    initHeader(root);
    initCarousels(root);
    initStories(root);
    initHeroVideo(root);
    initMarquee(root);
    initReveal(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* theme editor */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });
})();
